import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Globe, RefreshCw, AlertTriangle, ExternalLink, Eye, Edit3, Trash2, Database, UserPlus, CheckSquare, XSquare, Clock, Link, FileEdit, CheckCircle, Send, Calculator, Hash, History, DollarSign, X, Loader2, Image } from 'lucide-react';
import EavList from '../../components/EavList';
import BatchUpdateModal from '../../components/BatchUpdateModal';
import QaCheckModal from './QaCheckModal';
import ErpProductSyncModal from './ErpProductSyncModal';

import { useTranslation } from '../../context/LanguageContext';
import UserSelect from '../../components/UserSelect';
import ChannelAttributeSetSelector from './ChannelAttributeSetSelector';
import { usePermission } from '../../context/PermissionContext';
import { useNotification } from '../../context/NotificationContext';
import ChannelProductEditStatusModal from './ChannelProductEditStatusModal';
import ResponsiblePersonsCell, { useBatchResponsiblePersons } from '../../components/ResponsiblePersonsCell';
import BatchAssignResponsibleModal from '../../components/BatchAssignResponsibleModal';
import BatchRemoveResponsibleModal from '../../components/BatchRemoveResponsibleModal';
import TaskProgressModal from '../../components/TaskProgressModal';

/**
 * 渠道产品列表页（统一 EAV 版）
 * 列配置从后端动态获取，替代硬编码。
 */

const ChannelTypeBadge = ({ type }) => {
  const colors = {
    magento2: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    shopify: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  };
  const labels = { magento2: 'M2', shopify: 'SH' };
  return (
    <span className={`inline-flex items-center text-xs font-medium px-1.5 py-0.5 rounded ${colors[type] || 'bg-slate-100 text-slate-600'}`}>
      {labels[type] || type}
    </span>
  );
};

const SyncStatusBadge = ({ status, lastError, t, storeUrl, urlKey }) => {
  const colors = {
    not_synced: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400',
    pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    synced: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };
  const icons = {
    synced: <ExternalLink size={12} />,
    failed: <AlertTriangle size={12} />,
    pending: <RefreshCw size={12} className="animate-spin" />,
    not_synced: <RefreshCw size={12} />,
  };
  // 已同步父产品 → 渲染跳转链接
  const linkHref = storeUrl && urlKey ? `${storeUrl.replace(/\/+$/, '')}/${urlKey}.html` : null;
  const badge = (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${colors[status] || colors.not_synced}`}
      title={status === 'failed' && lastError ? lastError : undefined}>
      {icons[status] || icons.not_synced}
      {t(`channel.status_${status || 'not_synced'}`)}
    </span>
  );
  return linkHref ? (
    <a href={linkHref} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
      {badge}
    </a>
  ) : badge;
};

const QaStatusBadge = ({ status, t }) => {
  const config = {
    pending: { bg: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: Clock, label: t('qa_pending') },
    processing: { bg: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Loader2, label: t('qa_processing') || 'Processing' },
    pass: { bg: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckSquare, label: t('qa_pass') },
    fail: { bg: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: XSquare, label: t('qa_fail') },
  };
  const cfg = config[status] || config.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${cfg.bg}`}>
      <Icon size={12} />
      {cfg.label}
    </span>
  );
};

const EditStatusBadge = ({ status, t }) => {
  const config = {
    draft: { bg: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: FileEdit, label: t('edit_status_draft') || 'Draft' },
    processing: { bg: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Loader2, label: t('edit_status_processing') || 'Processing' },
    edited: { bg: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle, label: t('edit_status_edited') || 'Edited' },
  };
  const cfg = config[status] || config.draft;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${cfg.bg}`}>
      <Icon size={12} />
      {cfg.label}
    </span>
  );
};

const ChannelEavList = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [storeAttributes, setStoreAttributes] = useState(null);
  const [batchUpdateIds, setBatchUpdateIds] = useState(null);
  const [qaModalItem, setQaModalItem] = useState(null);
  const [qaRefreshKey, setQaRefreshKey] = useState(0);
  const [listKey, setListKey] = useState(0);
  const [editStatusModalItem, setEditStatusModalItem] = useState(null);
  const [syncStatusModal, setSyncStatusModal] = useState(null);
  const [editStatusRefreshKey, setEditStatusRefreshKey] = useState(0);
  const [erpSyncItem, setErpSyncItem] = useState(null);
  const [pageItemIds, setPageItemIds] = useState([]);
  const [itemGroupMap, setItemGroupMap] = useState({}); // entity_id → item_group_id
  const [batchAssignIds, setBatchAssignIds] = useState(null);
  const [batchRemoveIds, setBatchRemoveIds] = useState(null);

  const [pullResult, setPullResult] = useState(null);
  const [pulling, setPulling] = useState(false);
  const [loadingButtons, setLoadingButtons] = useState({}); // { '${entityId}:online': true, '${entityId}:price': true }

  // ── 批量推送至渠道 ──
  const handleBatchSync = async (selectedIds) => {
    // 只保留父产品（item_group_id === null），子产品随父一起推送
    const parentIds = selectedIds.filter(id => id in itemGroupMap && itemGroupMap[id] === null);
    const total = parentIds.length;
    if (total === 0) return;

    progressStep(t('channel.push_to_channel') || '推送至渠道', `准备推送 ${total} 个父产品...`);

    let success = 0;
    let fail = 0;
    const errors = [];

    for (let i = 0; i < total; i++) {
      const id = parentIds[i];
      progressStep(
        t('channel.push_to_channel') || '推送至渠道',
        `正在推送 ${i + 1}/${total}...`
      );
      try {
        const res = await fetch(`/api/channel/eav/products/${id}/sync`, { method: 'POST' });
        const data = await res.json();
        if (data.status === 'success') {
        } else {
          fail++;
          errors.push(`#${id}: ${data.message}`);
        }
      } catch (e) {
        fail++;
        errors.push(`#${id}: ${e.message}`);
      }
    }

    hideProgress();

    let msg = `完成：${success} 成功`;
    if (fail > 0) {
      msg += `，${fail} 失败`;
      if (errors.length > 0) {
        msg += `（${errors[0].substring(0, 60)}）`;
      }
    }
    notify(msg, fail === 0 ? 'success' : 'error');
    setListKey(k => k + 1);
  };

  // ── 批量更新在线属性 ──
  const handleBatchUpdateOnline = async (selectedIds) => {
    const total = selectedIds.length;
    if (total === 0) return;

    progressStep(t('channel.update_online') || '更新在线属性', `准备更新 ${total} 个产品...`);

    let success = 0;
    let fail = 0;
    const errors = [];

    for (let i = 0; i < total; i++) {
      const id = selectedIds[i];
      progressStep(
        t('channel.update_online') || '更新在线属性',
        `正在刷新 ${i + 1}/${total}...`
      );
      try {
        const res = await fetch(`/api/channel/eav/products/${id}/update-online`, { method: 'POST' });
        const data = await res.json();
        if (data.status === 'success') {
          success++;
        } else {
          fail++;
          errors.push(`#${id}: ${data.message}`);
        }
      } catch (e) {
        fail++;
        errors.push(`#${id}: ${e.message}`);
      }
    }

    hideProgress();

    let msg = `完成：${success} 成功`;
    if (fail > 0) {
      msg += `，${fail} 失败`;
      if (errors.length > 0) {
        msg += `（${errors[0].substring(0, 60)}）`;
      }
    }
    notify(msg, fail === 0 ? 'success' : 'error');
    setListKey(k => k + 1);
  };

  // ── 批量更新图片（串行逐个处理，不并发）──
  const handleBatchUpdateImages = async (selectedIds) => {
    const total = selectedIds.length;
    if (total === 0) return;

    progressStep(t('channel.update_images') || '更新图片', `准备更新 ${total} 个产品...`);

    let success = 0;
    let fail = 0;
    const errors = [];

    for (let i = 0; i < total; i++) {
      const id = selectedIds[i];
      progressStep(
        t('channel.update_images') || '更新图片',
        `正在替换图片 ${i + 1}/${total}...`
      );
      try {
        const res = await fetch(`/api/channel/eav/products/${id}/update-images`, { method: 'POST' });
        const data = await res.json();
        if (data.status === 'success') {
          success++;
        } else {
          fail++;
          errors.push(`#${id}: ${data.message}`);
        }
      } catch (e) {
        fail++;
        errors.push(`#${id}: ${e.message}`);
      }
    }

    hideProgress();

    let msg = `完成：${success} 成功`;
    if (fail > 0) {
      msg += `，${fail} 失败`;
      if (errors.length > 0) {
        msg += `（${errors[0].substring(0, 60)}）`;
      }
    }
    notify(msg, fail === 0 ? 'success' : 'error');
    setListKey(k => k + 1);
  };

  // ── 批量更新价格 ──
  const handleBatchUpdatePrice = async (selectedIds) => {
    const total = selectedIds.length;
    if (total === 0) return;

    progressStep(t('channel.update_price') || '更新价格', `准备更新 ${total} 个产品...`);

    let success = 0;
    let fail = 0;
    const errors = [];

    for (let i = 0; i < total; i++) {
      const id = selectedIds[i];
      progressStep(
        t('channel.update_price') || '更新价格',
        `正在更新价格 ${i + 1}/${total}...`
      );
      try {
        const res = await fetch(`/api/channel/eav/products/${id}/update-price`, { method: 'POST' });
        const data = await res.json();
        if (data.status === 'success') {
          success++;
        } else {
          fail++;
          errors.push(`#${id}: ${data.message}`);
        }
      } catch (e) {
        fail++;
        errors.push(`#${id}: ${e.message}`);
      }
    }

    hideProgress();

    let msg = `完成：${success} 成功`;
    if (fail > 0) {
      msg += `，${fail} 失败`;
      if (errors.length > 0) {
        msg += `（${errors[0].substring(0, 60)}）`;
      }
    }
    notify(msg, fail === 0 ? 'success' : 'error');
    setListKey(k => k + 1);
  };

  // ── 进度弹窗 ──
  const [progress, setProgress] = useState({ isOpen: false, title: '', message: '' });
  const progressStep = useCallback((title, message) => {
    setProgress({ isOpen: true, title, message });
  }, []);
  const hideProgress = useCallback(() => {
    setProgress({ isOpen: false, title: '', message: '' });
  }, []);
  // 同步日志功能已移除，前端 UI 通知替代数据库存储

  // ── 动态列配置（后台静默加载，失败不阻塞页面） ──
  const [columnConfig, setColumnConfig] = useState({ columns: [] });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/channel/eav/products/column-config');
        const data = await res.json();
        if (data.status === 'success') {
          setColumnConfig(data.data);
        }
      } catch (e) {
        console.error('Failed to load column config', e);
      }
    })();
  }, []);

  // ── 生成 URL Key ──
  const handleGenerateUrlKey = async (entityId) => {
    progressStep('生成 URL Key', '正在处理...');
    try {
      const res = await fetch('/api/channel/eav/products/generate-url-key', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entity_id: entityId }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        notify('URL Key 生成成功', 'success');
        setListKey(k => k + 1);
      } else {
        notify(data.message || '生成 URL Key 失败', 'error');
      }
    } catch (e) {
      notify('生成 URL Key 失败: ' + e.message, 'error');
    } finally {
      hideProgress();
    }
  };

  // ── 从店铺拉取产品 ──
  const handlePullFromStore = async () => {
    if (!selectedStoreId) {
      notify(t('channel.select_store_first') || '请先选择店铺', 'error');
      return;
    }
    setPulling(true);
    setPullResult(null);

    const PAGE_SIZE = 100;
    let totalCount = 0;
    let totalImported = 0;
    let totalSkipped = 0;
    const allErrors = [];

    try {
      // 第一步：获取总数
      progressStep(t('channel.pull_from_store'), t('channel.pull_fetching_count') || '获取产品总数...');
      const countRes = await fetch('/api/channel/eav/products/pull-count?store_id=' + parseInt(selectedStoreId));
      const countData = await countRes.json();
      if (countData.status !== 'success') {
        notify(countData.message || '获取产品数量失败', 'error');
        setPulling(false);
        return;
      }
      totalCount = countData.data.total_count;
      const totalPages = Math.ceil(totalCount / PAGE_SIZE);

      // 第二步：逐页拉取
      for (let page = 1; page <= totalPages; page++) {
        progressStep(
          t('channel.pull_from_store'),
          `正在拉取 ${Math.min(page * PAGE_SIZE, totalCount)}/${totalCount}...`
        );
        const res = await fetch('/api/channel/eav/products/pull', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ store_id: parseInt(selectedStoreId), page }),
        });
        const data = await res.json();
        if (data.status === 'success') {
          totalImported += data.data.imported || 0;
          totalSkipped += data.data.skipped || 0;
          if (data.data.errors) allErrors.push(...data.data.errors);
        } else {
          allErrors.push(`第 ${page} 页: ${data.message}`);
        }
      }

      hideProgress();
      setPullResult({ imported: totalImported, skipped: totalSkipped, errors: allErrors });
      notify(`拉取完成：${totalImported} 个新增，${totalSkipped} 个跳过`, 'success');
      setListKey(k => k + 1);
    } catch (e) {
      hideProgress();
      setPullResult({ imported: totalImported, skipped: totalSkipped, errors: [...allErrors, e.message] });
      notify(t('common.network_error'), 'error');
    } finally {
      setPulling(false);
    }
  };
  // ── 批量质量检查 ──
  const handleBatchQaCheck = async (selectedIds) => {
    setQaModalItem({ entityIds: selectedIds });
  };

  // ── 批量编辑状态 ──
  const handleBatchEditStatus = async (selectedIds) => {
    setEditStatusModalItem({ entityIds: selectedIds });
  };

  // ── 批量重设同步状态 ──
  const handleBatchUpdateSyncStatus = async (selectedIds) => {
    setSyncStatusModal(selectedIds);
  };

  // ── 批量生成 URL Key ──
  const handleBatchGenerateUrlKey = async (selectedIds) => {
    progressStep('批量生成 URL Key', '准备处理...');
    try {
      progressStep('批量生成 URL Key', `正在处理 ${selectedIds.length} 个产品...`);
      const res = await fetch('/api/channel/eav/products/generate-url-key', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entity_ids: selectedIds }),
      });
      const data = await res.json();
      if (data.status === 'success' || data.status === 'partial') {
        let msg = '';
        if (data.data) {
          if (data.data.success > 0) msg += `${data.data.success} 个生成成功`;
          if (data.data.errors && data.data.errors.length > 0) {
            msg += `，${data.data.errors.length} 个失败`;
            const firstErr = data.data.errors[0];
            msg += `（如：${firstErr.message?.substring(0, 40) || firstErr}）`;
          }
          if (data.data.skipped && data.data.skipped.length > 0) msg += `，${data.data.skipped.length} 个跳过：${data.data.skip_message || ''}`;
        }
        progressStep('批量生成 URL Key', '处理完成，更新列表...');
        await new Promise(r => setTimeout(r, 300));
        if (data.status === 'success') {
          notify(msg || '批量生成 URL Key 完成', 'success');
        } else {
          notify(msg || '部分生成失败', 'error');
        }
        setListKey(k => k + 1);
      } else {
        notify(data.message || '操作失败', 'error');
      }
    } catch (e) {
      notify('批量生成 URL Key 失败: ' + e.message, 'error');
    } finally {
      hideProgress();
    }
  };

  // ── 重算公式字段 ──
  const handleRecalculateFormulas = async (selectedIds) => {
    if (!selectedStoreId) {
      notify(t('channel.select_store_first') || '请先选择店铺', 'error');
      return;
    }
    progressStep('重算公式', `正在计算 ${selectedIds.length} 个产品...`);
    try {
      const res = await fetch('/api/channel/eav/products/recalculate-formulas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ store_id: parseInt(selectedStoreId), entity_ids: selectedIds }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        const { success, errors } = data.data || {};
        let msg = success + ' product(s) recalculated.';
        if (errors && errors.length > 0) {
          msg += ' ' + errors.length + ' failed.';
          const firstErr = errors[0];
          const errDetail = firstErr.entity_id ? '#' + firstErr.entity_id + ': ' + firstErr.message : firstErr.message;
          msg += ' ' + errDetail.substring(0, 60);
          notify(msg, 'error');
        } else {
          notify(msg, 'success');
        }
      } else {
        notify(data.message || '操作失败', 'error');
      }
    } catch (e) {
      notify('重算公式失败: ' + e.message, 'error');
    } finally {
      hideProgress();
    }
  };

  // ── 负责人批量加载 ──
  const entityTypeForApi = 'channel/eav/product';
  const { lookup: responsibleLookup, loading: responsibleLoading } = useBatchResponsiblePersons(entityTypeForApi, pageItemIds);
  const canEdit = hasPermission('channel_products', 'edit');
  const canAssign = hasPermission('channel_products', 'assign');
  const canUpdateOnline = hasPermission('channel_products', 'update_online_attributes');
  const canUpdateImages = hasPermission('channel_products', 'update_images');

  // 加载店铺列表用于筛选
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/channel/stores');
        const data = await res.json();
        if (data.status === 'success') setStores(data.data.stores || []);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  // 选定店铺后加载其已同步属性（用于列配置和筛选面板）
  useEffect(() => {
    if (!selectedStoreId) {
      setStoreAttributes(null);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`/api/channel/attributes/store-attributes?store_id=${selectedStoreId}`);
        const data = await res.json();
        if (data.status === 'success') {
          setStoreAttributes(data.data);
        }
      } catch (e) { /* ignore */ }
    })();
  }, [selectedStoreId]);

  // ── 动态构建 flatColumns ──
  const flatColumns = useMemo(() => {
    const cols = columnConfig.columns || [];
    return cols.map(col => ({
      code: col.code,
      label: col.label || col.code,
      inputType: col.input_type === 'Dropdown' || col.input_type === 'ForeignDropdown' ? 'Dropdown' : col.input_type,
    }));
  }, [columnConfig]);

  const defaultVisible = useMemo(() => {
    // 固定默认可见列
    const preferred = ['entity_id', 'image', 'channel_type', 'sku', 'erp_sku', 'edit_status', 'qa_status', 'url_key_status', 'sync_status', 'updated_at'];
    return preferred.filter(c => flatColumns.find(fc => fc.code === c));
  }, [flatColumns]);

  // ── 通用 Dropdown 渲染器 ──
  const genericDropdownRenderer = useCallback((options) => {
    return (col, value, onChange) => (
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || '')}
        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 dark:text-white"
      >
        <option value="">{t('common.all')}</option>
        {Object.entries(options || {}).map(([optVal, optLabel]) => (
          <option key={optVal} value={optVal}>{optLabel}</option>
        ))}
      </select>
    );
  }, [t]);

  // ── 日期范围渲染器 ──
  const dateRangeRenderer = useCallback((col, value, onChange, allFilters, setFilters) => {
    const fromKey = col.code + '_from';
    const toKey = col.code + '_to';
    return (
      <div className="space-y-2">
        <input type="date" value={allFilters[fromKey] || ''}
          onChange={e => setFilters({...allFilters, [fromKey]: e.target.value})}
          className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white" />
        <input type="date" value={allFilters[toKey] || ''}
          onChange={e => setFilters({...allFilters, [toKey]: e.target.value})}
          className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white" />
      </div>
    );
  }, []);

  // ── 动态筛选自定义渲染 ──
  const filterCustomRenderers = useMemo(() => {
    const renderers = {};

    // 特殊组件（没有通用化的）
    renderers.attribute_set_id = (col, value, onChange) => (
      <ChannelAttributeSetSelector
        value={value ? parseInt(value, 10) : ''}
        entityType={['magento2', 'shopify']}
        onChange={(val) => onChange(val ? String(val) : '')}
      />
    );
    renderers.created_by = (col, value, onChange) => (
      <UserSelect value={value} onChange={onChange} />
    );
    renderers.responsible_persons = (col, value, onChange) => (
      <UserSelect value={value} onChange={onChange} />
    );
    renderers.store_id = (col, value, onChange) => (
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || '')}
        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 dark:text-white"
      >
        <option value="">{t('common.all')}</option>
        {stores.map(s => (
          <option key={s.auth_id} value={s.auth_id}>{s.store_name}</option>
        ))}
      </select>
    );

    // 动态生成通用渲染器
    (columnConfig.columns || []).forEach(col => {
      // 跳过已有特殊渲染器的
      if (renderers[col.code]) return;
      if (col.code === 'created_at' || col.code === 'updated_at') {
        renderers[col.code] = dateRangeRenderer;
      } else if (col.input_type === 'Dropdown' && col.options) {
        renderers[col.code] = genericDropdownRenderer(col.options);
      }
      // 文本字段用默认输入框（EavList 自带）
    });

    return renderers;
  }, [columnConfig, stores, dateRangeRenderer, genericDropdownRenderer, t]);

  // ── 操作按钮渲染 ──
  const renderActions = (item, helpers) => {
    const { canView, canEdit, canDelete, handleDelete, page, pageSize } = helpers;
    return (
      <>
        {canView && (
          <button onClick={() => navigate(`/channel/eav-products/view/${item.entity_id}`)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
            title={t('common.view')}><Eye size={16} /></button>
        )}
        {canEdit && (
          <button onClick={() => navigate(`/channel/eav-products/edit/${item.entity_id}`, { state: { fromPage: page, fromPageSize: pageSize } })}
            className="p-1.5 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            title={t('common.edit')}><Edit3 size={16} /></button>
        )}
        {hasPermission('channel_products', 'qa_check') && (
          <button onClick={() => setQaModalItem({ entityId: item.entity_id, sku: item.sku, qaStatus: item.qa_status })}
            className={`p-1.5 rounded-lg transition-colors ${
              item.qa_status === 'pass' ? 'text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20' :
              item.qa_status === 'fail' ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20' :
              'text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20'
            }`}
            title={t('qa_perform') || 'QA Check'}>
            <CheckSquare size={16} />
          </button>
        )}
        {hasPermission('channel_products', 'generate_url_key') && item.channel_type === 'magento2' && (
          <button
            onClick={() => handleGenerateUrlKey(item.entity_id)}
            className={`p-1.5 rounded-lg transition-colors ${
              item.url_key_status === 'generated' ? 'text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20' :
              'text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20'
            }`}
            title={item.url_key_status === 'generated' ? (t('url_key_generated') || 'URL Key Generated') : (t('url_key_generate') || 'Generate URL Key')}>
            <Hash size={16} />
          </button>
        )}
        {hasPermission('channel_products', 'edit_status') && (
          <button onClick={() => setEditStatusModalItem({ entityId: item.entity_id, sku: item.sku, editStatus: item.edit_status })}
            className={`p-1.5 rounded-lg transition-colors ${
              item.edit_status === 'edited' ? 'text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20' :
              'text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20'
            }`}
            title={t('edit_status_perform') || 'Edit Status'}>
            <FileEdit size={16} />
          </button>
        )}
        {hasPermission('channel_products', 'recalculate_formulas') && (
          <button onClick={async () => {
            try {
              const res = await fetch('/api/channel/eav/products/recalculate-formulas', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ store_id: parseInt(item.store_id), entity_ids: [item.entity_id] }),
              });
              const data = await res.json();
              if (data.status === 'success') {
                const { errors } = data.data || {};
                if (errors && errors.length > 0) {
                  const firstErr = errors[0];
                  const errDetail = firstErr.entity_id ? '#' + firstErr.entity_id + ': ' + firstErr.message : firstErr.message;
                  notify(errDetail.substring(0, 80), 'error');
                } else {
                  notify('Recalculated successfully', 'success');
                }
              } else {
                notify(data.message || '操作失败', 'error');
              }
            } catch (e) {
              notify('重算公式失败: ' + e.message, 'error');
            }
          }}
            className="p-1.5 rounded-lg text-slate-500 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
            title={t('channel.recalculate_formulas') || 'Recalculate Formulas'}>
            <Calculator size={16} />
          </button>
        )}
        {hasPermission('channel_products', 'sync') && (
          <button onClick={async () => {
            const entityId = item.entity_id;
            progressStep(t('channel.sync') || '同步产品', '正在推送产品...');
            try {
              const res = await fetch(`/api/channel/eav/products/${entityId}/sync`, {
                method: 'POST',
              });
              const data = await res.json();
              if (data.status === 'success') {
                progressStep(t('channel.sync') || '同步产品', '推送成功，更新列表...');
                setListKey(k => k + 1);
                notify(t('common.success') || 'Success', 'success');
              } else {
                notify(data.message || t('channel.sync_failed') || 'Sync failed', 'error');
              }
            } catch (e) {
              notify(t('channel.sync_failed') || 'Sync failed', 'error');
            } finally {
              hideProgress();
            }
          }}
            className="p-1.5 rounded-lg text-slate-500 hover:text-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 transition-colors"
            title={t('channel.sync') || 'Sync'}>
            <Send size={16} />
          </button>
        )}
        {canUpdateOnline && (
          <button
            onClick={async () => {
              const key = item.entity_id + ':online';
              setLoadingButtons(prev => ({ ...prev, [key]: true }));
              try {
                const res = await fetch(`/api/channel/eav/products/${item.entity_id}/update-online`, {
                  method: 'POST',
                });
                const data = await res.json();
                if (data.status === 'success') {
                  setListKey(k => k + 1);
                  notify(t('common.success') || 'Success', 'success');
                } else {
                  notify(data.message || '刷新失败', 'error');
                }
              } catch (e) {
                notify('刷新失败: ' + e.message, 'error');
              } finally {
                setLoadingButtons(prev => ({ ...prev, [key]: false }));
              }
            }}
            disabled={loadingButtons[item.entity_id + ':online']}
            className={`p-1.5 rounded-lg transition-colors ${
              loadingButtons[item.entity_id + ':online']
                ? 'text-cyan-400 bg-cyan-50 dark:bg-cyan-900/20 cursor-not-allowed'
                : 'text-slate-500 hover:text-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-900/20'
            }`}
            title={t('channel.update_online') || '刷新远程'}>
            <RefreshCw size={16} className={loadingButtons[item.entity_id + ':online'] ? 'animate-spin' : ''} />
          </button>
        )}
        {canEdit && item.channel_type === 'magento2' && (
          <button
            onClick={async () => {
              const key = item.entity_id + ':price';
              setLoadingButtons(prev => ({ ...prev, [key]: true }));
              try {
                const res = await fetch(`/api/channel/eav/products/${item.entity_id}/update-price`, {
                  method: 'POST',
                });
                const data = await res.json();
                if (data.status === 'success') {
                  setListKey(k => k + 1);
                  notify(t('common.success') || 'Success', 'success');
                } else {
                  notify(data.message || '价格更新失败', 'error');
                }
              } catch (e) {
                notify('价格更新失败: ' + e.message, 'error');
              } finally {
                setLoadingButtons(prev => ({ ...prev, [key]: false }));
              }
            }}
            disabled={loadingButtons[item.entity_id + ':price']}
            className={`p-1.5 rounded-lg transition-colors ${
              loadingButtons[item.entity_id + ':price']
                ? 'text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 cursor-not-allowed'
                : 'text-slate-500 hover:text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
            }`}
            title={t('channel.update_price') || '更新价格'}>
            <DollarSign size={16} className={loadingButtons[item.entity_id + ':price'] ? 'animate-spin' : ''} />
          </button>
        )}
        {canUpdateImages && item.channel_type === 'magento2' && (
          <button
            onClick={async () => {
              const key = item.entity_id + ':images';
              setLoadingButtons(prev => ({ ...prev, [key]: true }));
              try {
                const res = await fetch(`/api/channel/eav/products/${item.entity_id}/update-images`, {
                  method: 'POST',
                });
                const data = await res.json();
                if (data.status === 'success') {
                  setListKey(k => k + 1);
                  notify(data.message || '图片已更新', 'success');
                } else {
                  notify(data.message || '更新图片失败', 'error');
                }
              } catch (e) {
                notify('更新图片失败: ' + e.message, 'error');
              } finally {
                setLoadingButtons(prev => ({ ...prev, [key]: false }));
              }
            }}
            disabled={loadingButtons[item.entity_id + ':images']}
            className={`p-1.5 rounded-lg transition-colors ${
              loadingButtons[item.entity_id + ':images']
                ? 'text-purple-400 bg-purple-50 dark:bg-purple-900/20 cursor-not-allowed'
                : 'text-slate-500 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20'
            }`}
            title={t('channel.update_images') || '更新图片'}>
            <Image size={16} className={loadingButtons[item.entity_id + ':images'] ? 'animate-spin' : ''} />
          </button>
        )}
        {canAssign && (
          <button onClick={() => navigate(`/channel/eav-products/${item.entity_id}/assign`)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
            title={t('common.assign_responsible')}><UserPlus size={16} /></button>
        )}
        <button onClick={() => setErpSyncItem({ entityId: item.entity_id, sku: item.sku, productId: item.product_id, erpSku: item.erp_sku })}
          className={`p-1.5 rounded-lg transition-colors ${
            item.product_id ? 'text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20' : 'text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20'
          }`}
          title={t('channel.sync_erp_product') || 'Link ERP Product'}>
          <Link size={16} />
        </button>
        {canDelete && (
          <button onClick={() => handleDelete(item.entity_id)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            title={t('common.delete')}><Trash2 size={16} /></button>
        )}
      </>
    );
  };

  // ── 动态单元格渲染 ──
  const renderCell = (item, col) => {
    if (col.code === 'channel_type') {
      return <ChannelTypeBadge type={item.channel_type || 'magento2'} />;
    }
    if (col.code === 'sync_status') {
      // 已同步父产品 → 传 storeUrl + urlKey 渲染跳转链接
      const urlKey = item.item_group_id === null && item.attributes
        ? Object.entries(item.attributes).find(([k]) => /^m2_\d+_url_key$/.test(k))?.[1]
        : null;
      return (
        <SyncStatusBadge
          status={item.sync_status}
          lastError={item.last_error}
          t={t}
          storeUrl={item.store_url}
          urlKey={urlKey}
        />
      );
    }
    if (col.code === 'store_id') {
      return <span>{item.store_name || `#${item.store_id}`}</span>;
    }
    if (col.code === 'attribute_set_id') {
      return <span>{item.attribute_set_name || `#${item.attribute_set_id}`}</span>;
    }
    if (col.code === 'item_group_id') {
      return <span>{item.parent_sku || `#${item.item_group_id}`}</span>;
    }
    if (col.code === 'name') {
      return (
        <span className="text-slate-800 dark:text-slate-200 max-w-[200px] truncate block">
          {item.attributes?.name || '-'}
        </span>
      );
    }
    if (col.code === 'qa_status') {
      return <QaStatusBadge status={item.qa_status} t={t} />;
    }
    if (col.code === 'edit_status') {
      return <EditStatusBadge status={item.edit_status} t={t} />;
    }
    if (col.code === 'responsible_persons') {
      return <ResponsiblePersonsCell entityType="channel/eav/product" entityId={item.entity_id} lookup={responsibleLookup} />;
    }
    if (col.code === 'url_key_status') {
      const urlKeyConfig = {
        generated: { bg: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle, label: t('url_key_generated') || 'Generated' },
        pending: { bg: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: Clock, label: t('common.pending') || 'Pending' },
      };
      const cfg = urlKeyConfig[item.url_key_status] || { bg: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400', icon: XSquare, label: item.url_key_status || '-' };
      const IconCmp = cfg.icon || XSquare;
      return (
        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${cfg.bg}`}>
          <IconCmp size={12} />
          {cfg.label}
        </span>
      );
    }
    return null; // fallback to default rendering
  };

  // ── 排除筛选的列 ──
  const excludeFilterColumns = useMemo(() => {
    return ['store_id'];
  }, []);

  return (
    <>
    <EavList
      key={listKey}
      entityType="channel/eav"
      attributeSetId={0}
      flatColumns={flatColumns}
      defaultVisible={defaultVisible}
      titleKey="channel.eav_products"
      searchKey="channel.search"
      pageTotalKey="channel.page_total"
      confirmDeleteKey="channel.confirm_delete"
      confirmDeleteSingleKey="channel.confirm_delete_single"
      actionsLabelKey="channel.actions"
      apiPrefix="/api/channel/eav/products"
      routes={{
        add: '/channel/eav-products/new',
        view: (id) => `/channel/eav-products/view/${id}`,
        edit: (id) => `/channel/eav-products/edit/${id}`,
      }}
      treeField="item_group_id"
      treeNodeIdField="entity_id"
      afterLoad={(items) => {
        setPageItemIds(items.map(item => item.entity_id).filter(id => id != null));
        const map = {};
        for (const item of items) {
          if (item.entity_id != null) map[item.entity_id] = item.item_group_id ?? null;
        }
        setItemGroupMap(prev => ({...prev, ...map}));
      }}
      usePageSizeChange={true}
      canAdd={hasPermission('channel_products', 'add')}
      canEdit={hasPermission('channel_products', 'edit')}
      canDelete={hasPermission('channel_products', 'delete')}
      canView={hasPermission('channel_products', 'view')}
      onBatchUpdateCustom={setBatchUpdateIds}
      filterCustomRenderers={filterCustomRenderers}
      excludeFilterColumns={excludeFilterColumns}
      filterBarExtra={(filters, setFilters) => (
        <div className="flex items-center gap-3 w-full md:w-auto" key="store-filter">
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap">
            <Database size={12} className="inline mr-1" />
            {t('channel.store')}:
          </label>
          <select value={selectedStoreId}
            onChange={e => {
              const val = e.target.value;
              setSelectedStoreId(val);
              setFilters({...filters, store_id: val});
            }}
            className="w-full md:min-w-[130px] rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm dark:text-slate-200 focus:ring-2 focus:ring-blue-500">
            <option value="">{t('channel.all_stores')}</option>
            {stores.map(s => (
              <option key={s.auth_id} value={s.auth_id}>{s.store_name}</option>
            ))}
          </select>
          {hasPermission('channel_products', 'sync') && (
            <button
              onClick={handlePullFromStore}
              disabled={!selectedStoreId || pulling}
              className="flex items-center gap-1.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 px-3 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 text-sm font-medium transition-colors dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('channel.pull_from_store') || '从店铺拉取产品'}
            >
              {pulling ? <RefreshCw size={16} className="animate-spin" /> : <Download size={16} />}
              Pull
            </button>
          )}
        </div>
      )}
      externalAttributeConfig={storeAttributes}
      onBatchAssign={canAssign ? setBatchAssignIds : undefined}
      onBatchQcUpdate={hasPermission('channel_products', 'qa_check') ? handleBatchQaCheck : undefined}
      onBatchEditStatus={hasPermission('channel_products', 'edit_status') ? handleBatchEditStatus : undefined}
      onBatchUpdateSyncStatus={hasPermission('channel_products', 'update_sync_status') ? handleBatchUpdateSyncStatus : undefined}
      onBatchGenerateUrlKey={hasPermission('channel_products', 'generate_url_key') ? handleBatchGenerateUrlKey : undefined}
      onBatchRemoveResponsible={canAssign ? setBatchRemoveIds : undefined}
      onBatchRecalculateFormulas={handleRecalculateFormulas}
      onBatchPushToChannel={hasPermission('channel_products', 'sync') ? handleBatchSync : undefined}
      onBatchUpdateOnline={canUpdateOnline ? handleBatchUpdateOnline : undefined}
      onBatchUpdatePrice={canEdit ? handleBatchUpdatePrice : undefined}
      onBatchUpdateImages={canUpdateImages ? handleBatchUpdateImages : undefined}
      renderActions={renderActions}
      renderCell={renderCell}
    />
      {pullResult && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40" onClick={() => setPullResult(null)}>
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-3">
              {t('channel.pull_from_store') || '从店铺拉取产品'}
            </h3>
            <div className="space-y-2 text-sm">
              <p className="text-green-600 font-medium">{pullResult.imported}{t('channel.pull_imported') || ' 个新增'}</p>
              <p className="text-slate-600 dark:text-slate-400">{pullResult.skipped}{t('channel.pull_skipped') || ' 个跳过（SKU 已存在）'}</p>
              {pullResult.errors?.length > 0 && (
                <div className="max-h-40 overflow-y-auto mt-2 space-y-1">
                  <p className="text-red-500 font-medium">{pullResult.errors.length}{t('channel.pull_failed_count') || ' 个失败'}</p>
                  {pullResult.errors.slice(0, 10).map((err, i) => (
                    <p key={i} className="text-xs text-red-500">
                      {typeof err === 'string' ? err.substring(0, 80) : err?.message?.substring(0, 80) || JSON.stringify(err).substring(0, 80)}
                    </p>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={() => { setPullResult(null); setListKey(k => k + 1); }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                {t('common.close') || 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
      {batchUpdateIds && (
        <BatchUpdateModal
          entityType="channel/eav"
          apiPrefix="/api/channel/eav/products"
          selectedIds={batchUpdateIds}
          storeId={selectedStoreId}
          onClose={() => setBatchUpdateIds(null)}
          onSuccess={() => setBatchUpdateIds(null)}
        />
      )}
      {qaModalItem && (
        <QaCheckModal
          channelType={qaModalItem.channelType}
          entityId={qaModalItem.entityId}
          entityIds={qaModalItem.entityIds}
          sku={qaModalItem.sku}
          currentStatus={qaModalItem.qaStatus}
          onClose={() => setQaModalItem(null)}
          onSuccess={() => {
            setQaModalItem(null);
            setListKey(k => k + 1);
          }}
        />
      )}
      {editStatusModalItem && (
        <ChannelProductEditStatusModal
          channelType={editStatusModalItem.channelType}
          entityId={editStatusModalItem.entityId}
          entityIds={editStatusModalItem.entityIds}
          sku={editStatusModalItem.sku}
          currentStatus={editStatusModalItem.editStatus}
          onClose={() => setEditStatusModalItem(null)}
          onSuccess={() => {
            setEditStatusModalItem(null);
            setListKey(k => k + 1);
          }}
        />
      )}
      {erpSyncItem && (
        <ErpProductSyncModal
          entityId={erpSyncItem.entityId}
          sku={erpSyncItem.sku}
          productId={erpSyncItem.productId}
          erpSku={erpSyncItem.erpSku}
          onClose={() => setErpSyncItem(null)}
          onSuccess={() => {
            setErpSyncItem(null);
            setQaRefreshKey(k => k + 1);
          }}
        />
      )}
      {batchAssignIds && (
        <BatchAssignResponsibleModal
          entityType="channel/eav/product"
          selectedIds={batchAssignIds}
          onClose={() => setBatchAssignIds(null)}
          onSuccess={() => {
            setBatchAssignIds(null);
            setTimeout(() => setRpLookup(null), 100);
          }}
        />
      )}
      {batchRemoveIds && (
        <BatchRemoveResponsibleModal
          entityType="channel/eav/product"
          selectedIds={batchRemoveIds}
          onClose={() => setBatchRemoveIds(null)}
          onSuccess={() => {
            setBatchRemoveIds(null);
            setTimeout(() => setRpLookup(null), 100);
          }}
        />
      )}
      {syncStatusModal && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 z-50 flex items-center justify-center" onClick={() => setSyncStatusModal(null)}>
          <SyncStatusUpdateModalContent
            selectedIds={syncStatusModal}
            onClose={() => setSyncStatusModal(null)}
            onSuccess={() => {
              setSyncStatusModal(null);
              setListKey(k => k + 1);
            }}
            notify={notify}
          />
        </div>
      )}

      <TaskProgressModal
        isOpen={progress.isOpen}
        title={progress.title}
        message={progress.message}
      />
    </>
  );
};

/**
 * Sync Status Update Modal Content
 */
const SyncStatusUpdateModalContent = ({ selectedIds, onClose, onSuccess, notify }) => {
  const { t } = useTranslation();
  const [selectedStatus, setSelectedStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedStatus) {
      notify(t('common.select_status_hint') || '请选择目标状态', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/channel/eav/products/batch-update-sync-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entity_ids: selectedIds, status: selectedStatus }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        notify(t('channel.batch_update_success', { count: data.success }) || `同步状态已更新：${data.success} 个产品`, 'success');
        onSuccess();
      } else if (data.status === 'partial') {
        notify(t('channel.batch_update_partial', { success: data.success, errors: data.errors?.length || 0 }) || `部分成功：${data.success} 个更新，${data.errors?.length || 0} 个失败`, 'warning');
        onSuccess();
      } else {
        notify(data.message || t('common.error'), 'error');
      }
    } catch (e) {
      notify((t('channel.batch_update_failed') || '重设同步状态失败') + ': ' + e.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const statusOptions = [
    { value: 'pending', label: t('channel.sync_pending') || 'Pending', icon: Clock },
    { value: 'not_synced', label: t('channel.sync_not_synced') || 'Not Synced', icon: Link },
    { value: 'failed', label: t('channel.sync_failed') || 'Failed', icon: XSquare },
  ];

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">{t('channel.batch_update_sync_status') || '重设同步状态'}</h2>
        <button onClick={onClose}
          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
          <X size={18} />
        </button>
      </div>

      <div className="px-6 py-5 space-y-5">
        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 text-sm text-slate-600 dark:text-slate-400">
          {t('channel.batch_update_hint', { count: selectedIds.length }) || `已选择 ${selectedIds.length} 个产品，请选择目标同步状态`}
        </div>

        <div className="space-y-3">
          <label className="block text-sm font-medium text-slate-600 dark:text-slate-400">
            {t('common.select_status') || '目标状态'}
          </label>
          <div className="space-y-2">
            {statusOptions.map(opt => {
              const Icon = opt.icon;
              const isSelected = selectedStatus === opt.value;
              return (
                <button key={opt.value}
                  onClick={() => setSelectedStatus(opt.value)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all ${
                    isSelected
                      ? 'border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-sm'
                      : 'border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-500'
                  }`}>
                  <Icon size={18} />
                  <span className="text-sm font-medium">{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
        <button onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
          {t('common.cancel') || '取消'}
        </button>
        <button onClick={handleSubmit} disabled={submitting}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors inline-flex items-center gap-2">
          {submitting && <Loader2 size={14} className="animate-spin" />}
          {t('common.save') || '确认'}
        </button>
      </div>
    </div>
  );
};

/* ── 更新在线产品属性模态框 ── */
export default ChannelEavList;
